// src/components/ThreadCard.jsx (or wherever it lives)
import Link from "next/link";
import { relTime } from "@/utils/time"; // adjust if your alias differs

const CATEGORY_LABELS = {
  faq: "FAQ",
  support: "Support",
  announcements: "Announcements",
  fun: "Fun",
};

export default function ThreadCard({ thread }) {
  if (!thread) return null;

  const {
    _id,
    slug,
    canonicalUrl,
    title,
    body,
    summary,
    createdBy,
    createdAt,
    replyCount,
    category,
    type, // "discussion" | "article"
    pinned,
    locked,
    tags = [],
  } = thread;

  const href =
    canonicalUrl ||
    (_id ? (slug ? `/community/thread/${_id}/${slug}` : `/community/thread/${_id}`) : "#");

  const preview = type === "article" && summary ? summary : body || "";

  return (
    <div className="flex border border-border rounded bg-surface p-4 shadow-sm hover:shadow-md transition">
      <div className="flex flex-col items-center justify-start mr-4 w-10">
        <button className="text-textSecondary text-xs hover:text-primary" title="Upvote">
          ⬆
        </button>
        <span className="text-xs text-textSecondary" title="Replies">
          💬 {replyCount || 0}
        </span>
        <button className="text-textSecondary text-xs hover:text-primary" title="Downvote">
          ⬇
        </button>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-2">
          <Link
            href={href}
            className="flex-1 min-w-0 text-base sm:text-lg font-semibold text-primary hover:underline truncate"
            title={title}
          >
            {title}
          </Link>

          <div className="hidden md:flex items-center gap-1 shrink-0">
            {pinned && (
              <span
                className="px-2 py-0.5 text-[10px] rounded-full border border-border bg-background tracking-wide"
                title="Pinned"
              >
                📌 Pinned
              </span>
            )}
            {locked && (
              <span
                className="px-2 py-0.5 text-[10px] rounded-full border border-border bg-background tracking-wide"
                title="Locked (no replies)"
              >
                🔒 Locked
              </span>
            )}
            {type === "article" && (
              <span
                className="px-2 py-0.5 text-[10px] rounded-full border border-border bg-background tracking-wide"
                title="Article"
              >
                ARTICLE
              </span>
            )}
          </div>
        </div>

        {(pinned || locked || type === "article") && (
          <div className="mt-1 flex md:hidden items-center gap-2 text-xs text-textSecondary">
            {pinned && <span title="Pinned">📌</span>}
            {locked && <span title="Locked">🔒</span>}
            {type === "article" && <span title="Article">📰</span>}
          </div>
        )}

        <div className="mt-1 mb-1">
          <Link
            href={`/community/category/${category || "faq"}`}
            className="inline-block px-2 py-0.5 text-[11px] rounded-full bg-border text-textSecondary hover:opacity-90"
            title="View this category"
          >
            {CATEGORY_LABELS[category] || (category || "").toUpperCase()}
          </Link>
        </div>

        <p className="text-xs text-textSecondary">
          Posted by <span className="font-medium">{createdBy?.username || "Unknown"}</span> •
          <span className="inline md:hidden"> {relTime(createdAt)}</span>
          <span className="hidden md:inline"> {new Date(createdAt).toLocaleString()}</span>
        </p>

        {preview && (
          <p className="mt-2 text-sm text-textPrimary line-clamp-2" dir="auto">
            {preview}
          </p>
        )}

        {tags.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {tags.map((tag, idx) => (
              <span
                key={`${tag}-${idx}`}
                className="text-xs bg-border text-textPrimary px-2 py-1 rounded-full"
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
